import React, { useState } from 'react';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { formatEuDateTime } from '../utils/dateFormat';
import './WeaponLookup.css';

function WeaponLookup() {
  const { language } = useI18n();
  const { apiFetch } = useAuth();
  const [serial, setSerial] = useState('');
  const [weapon, setWeapon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [batchSerials, setBatchSerials] = useState('');
  const [batchResults, setBatchResults] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const t = language === 'da'
    ? {
      noWeapon: 'Ingen våben fundet med dette serienummer',
      failedLookup: 'Kunne ikke slå våben op',
      title: 'Weapon serial opslag',
      subtitle: 'Find våbeninformation via serienummer',
      serialNumber: 'Serienummer',
      serialPlaceholder: 'Indtast våbenets serienummer',
      searching: 'Søger...',
      lookup: 'Søg våben',
      tips: 'Tips',
      tip1: 'Tjek serienummeret, er det korrekt?',
      tip2: 'Serienumre skelner mellem store og små bogstaver',
      tip3: 'Sørg for, at våbnet blev fremstillet via systemet. Hvis ikke, kan det være et modificeret våben uden korrekt serienummer',
      weaponInfo: 'Våbeninformation',
      basicInfo: 'Basisinfo',
      serialLabel: 'Serienummer:',
      weaponName: 'Våbennavn:',
      type: 'Type:',
      location: 'Lokation:',
      time: 'Tidspunkt:',
      crafterInfo: 'Crafter information',
      craftedBy: 'Craftet af:',
      crafterId: 'Crafted ID:',
      currentOwner: 'Nuvaerende ejer',
      owner: 'Ejer:',
      registererInfo: 'Registrerings information',
      registerer: 'Registreret af:',
      registererId: 'Registrerings ID:',
      registeredAt: 'Registreret ved:',
      unknown: 'Ukendt',
      na: 'N/A',
      batchCheck: 'Batch Tjek',
      batchSerials: 'Serienumre (kommasepareret)',
      batchPlaceholder: 'Ex: serial1, serial2, serial3...',
      checkBatch: 'Tjek batch',
      batchResults: 'Batch resultater',
      accepted: 'Accepteret',
      denied: 'Afvist',
      deniedList: 'Afviste serienumre',
      noDenied: 'Alle serienumre blev accepteret'
    }
    : {
      noWeapon: 'No weapon found with this serial number',
      failedLookup: 'Failed to lookup weapon',
      title: 'Weapon Serial Lookup',
      subtitle: 'Find weapon information by serial number',
      serialNumber: 'Serial Number',
      serialPlaceholder: 'Enter weapon serial number',
      searching: 'Searching...',
      lookup: 'Lookup Weapon',
      tips: 'Tips',
      tip1: 'Check the serial number, is it correct?',
      tip2: 'Serial numbers are case-sensitive',
      tip3: 'Make sure the weapon was crafted through the system, if not it may be a modded weapon without proper serial',
      weaponInfo: 'Weapon Information',
      basicInfo: 'Basic Info',
      serialLabel: 'Serial Number:',
      weaponName: 'Weapon Name:',
      type: 'Type:',
      location: 'Location:',
      time: 'Time:',
      crafterInfo: 'Crafter Information',
      craftedBy: 'Crafted By:',
      crafterId: 'Crafter ID:',
      currentOwner: 'Current Owner',
      owner: 'Owner:',
      registererInfo: 'Registration Information',
      registerer: 'Registered By:',
      registererId: 'Registerer ID:',
      registeredAt: 'Registered At:',
      unknown: 'Unknown',
      na: 'N/A',
      batchCheck: 'Batch Check',
      batchSerials: 'Serial Numbers (comma-separated)',
      batchPlaceholder: 'Ex: serial1, serial2, serial3...',
      checkBatch: 'Check Batch',
      batchResults: 'Batch Results',
      accepted: 'Accepted',
      denied: 'Denied',
      deniedList: 'Denied Serial Numbers',
      noDenied: 'All serial numbers were accepted'
    };

  const handleLookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setWeapon(null);

    try {
      const response = await apiFetch(`/api/weapons/lookup?serial=${encodeURIComponent(serial)}`);

      if (response.status === 404) {
        setError(t.noWeapon);
        setLoading(false);
        return;
      }

      if (!response.ok) {
        throw new Error(t.failedLookup);
      }

      const data = await response.json();
      setWeapon(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBatchCheck = async (e) => {
    e.preventDefault();
    setBatchLoading(true);
    setBatchResults(null);

    try {
      const serials = batchSerials
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);

      if (serials.length === 0) {
        alert('Please enter at least one serial number');
        setBatchLoading(false);
        return;
      }

      const response = await apiFetch('/api/weapons/batch-check', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ serials })
      });

      if (!response.ok) {
        throw new Error(t.failedLookup);
      }

      const data = await response.json();
      setBatchResults(data);
    } catch (err) {
      alert(err.message);
    } finally {
      setBatchLoading(false);
    }
  };

  const handleDeleteWeapon = async (weaponData) => {
    setDeleting(true);
    try {
      const response = await apiFetch('/api/weapons/delete-from-inventory', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serial: weaponData.serial,
          location: weaponData.location
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete weapon');
      }

      alert(`Weapon deleted successfully from ${weaponData.location}`);
      
      // Refresh the batch results by removing the deleted weapon
      setBatchResults(prev => ({
        ...prev,
        denied: prev.denied - 1,
        deniedSerials: prev.deniedSerials.filter(item => item.serial !== weaponData.serial)
      }));
      
      setDeleteConfirm(null);
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="weapon-lookup">
      <div className="lookup-header">
        <h2>{t.title}</h2>
        <p>{t.subtitle}</p>
      </div>

      <form onSubmit={handleLookup} className="lookup-form">
        <div className="form-group">
          <label>{t.serialNumber}</label>
          <input
            type="text"
            placeholder={t.serialPlaceholder}
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            required
          />
        </div>

        <button type="submit" className="lookup-button" disabled={loading}>
          {loading ? t.searching : `${t.lookup}`}
        </button>
      </form>

      <div className="batch-section">
        <h3>{t.batchCheck}</h3>
        <form onSubmit={handleBatchCheck} className="lookup-form">
          <div className="form-group">
            <label>{t.batchSerials}</label>
            <textarea
              placeholder={t.batchPlaceholder}
              value={batchSerials}
              onChange={(e) => setBatchSerials(e.target.value)}
              rows="4"
              className="batch-textarea"
            />
          </div>

          <button type="submit" className="lookup-button" disabled={batchLoading}>
            {batchLoading ? t.searching : `${t.checkBatch}`}
          </button>
        </form>
      </div>

      {error && (
        <div className="alert error">
          <strong>{error}</strong>
          <div className="tips">
            <p><strong>{t.tips}:</strong></p>
            <ul>
              <li>{t.tip1}</li>
              <li>{t.tip2}</li>
              <li>{t.tip3}</li>
            </ul>
          </div>
        </div>
      )}

      {weapon && (
        <div className="weapon-result">
          <h3>{t.weaponInfo}</h3>

          <div className="weapon-details">
            <div className="detail-section">
              <h4>{t.basicInfo}</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="label">{t.serialLabel}</span>
                  <span className="value mono">{weapon.serial_number}</span>
                </div>
                <div className="detail-item">
                  <span className="label">{t.weaponName}</span>
                  <span className="value">{weapon.weapon_name || t.unknown}</span>
                </div>
                <div className="detail-item">
                  <span className="label">{t.type}</span>
                  <span className="value">{weapon.weapon_type || t.unknown}</span>
                </div>
                <div className="detail-item">
                  <span className="label">{t.location}</span>
                  {weapon.location ? (
                    <span className="value">
                      X: {weapon.location.x}, Y: {weapon.location.y}, Z: {weapon.location.z}
                    </span>
                  ) : (
                    <span className="value">{t.na}</span>
                  )}
                </div>
                <div className="detail-item">
                  <span className="label">{t.time}</span>
                  <span className="value">{formatEuDateTime(weapon.time, t.unknown)}</span>
                </div>
              </div>
            </div>

            <div className="detail-section">
              <h4>{t.crafterInfo}</h4>
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="label">{t.craftedBy}</span>
                  <span className="value">{weapon.crafter_name || t.unknown}</span>
                </div>
                <div className="detail-item">
                  <span className="label">{t.crafterId}</span>
                  <span className="value mono">{weapon.crafter_identifier || t.unknown}</span>
                </div>
              </div>
            </div>

            {weapon.current_owner && (
              <div className="detail-section">
                <h4>{t.currentOwner}</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">{t.owner}</span>
                    <span className="value mono">{weapon.current_owner}</span>
                  </div>
                </div>
              </div>
            )}

            {weapon.registerer_name && (
              <div className="detail-section">
                <h4>{t.registererInfo}</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="label">{t.registerer}</span>
                    <span className="value">{weapon.registerer_name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">{t.registererId}</span>
                    <span className="value mono">{weapon.registerer_identifier || t.unknown}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">{t.registeredAt}</span>
                    <span className="value">{formatEuDateTime(weapon.registered_at, t.unknown)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {batchResults && (
        <div className="batch-results">
          <h3>{t.batchResults}</h3>
          <div className="batch-stats">
            <div className="stat-card accepted">
              <div className="stat-number">{batchResults.accepted}</div>
              <div className="stat-label">{t.accepted}</div>
            </div>
            <div className="stat-card denied">
              <div className="stat-number">{batchResults.denied}</div>
              <div className="stat-label">{t.denied}</div>
            </div>
          </div>

          {batchResults.deniedSerials && batchResults.deniedSerials.length > 0 && (
            <div className="denied-list">
              <h4>{t.deniedList}</h4>
              <div className="serials-table">
                {batchResults.deniedSerials.map((item, idx) => (
                  <div key={idx} className="serial-row">
                    <span className="serial-index">{idx + 1}.</span>
                    <div className="serial-info">
                      <span className="serial-value mono">{item.serial}</span>
                      <span className="weapon-badge">{item.weaponName}</span>
                    </div>
                    <span className="location-text">{item.location}</span>
                    <button 
                      className="delete-btn"
                      onClick={() => setDeleteConfirm(item)}
                      disabled={deleting}
                      title="Delete this weapon"
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(!batchResults.deniedSerials || batchResults.deniedSerials.length === 0) && (
            <div className="all-accepted">
              <span className="checkmark">✓</span>
              <p>{t.noDenied}</p>
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>Confirm Deletion</h3>
            <p>Are you sure you want to delete this weapon?</p>
            <div className="delete-details">
              <p><strong>Serial:</strong> <span className="mono">{deleteConfirm.serial}</span></p>
              <p><strong>Weapon:</strong> {deleteConfirm.weaponName}</p>
              <p><strong>Location:</strong> {deleteConfirm.location}</p>
            </div>
            <p className="warning-text">This action cannot be undone!</p>
            <div className="modal-buttons">
              <button 
                className="cancel-btn"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                className="confirm-delete-btn"
                onClick={() => handleDeleteWeapon(deleteConfirm)}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WeaponLookup;


